import ApiClient from './apiClient';
import { logger } from '../logging';

export interface RefreshTokenResponse {
  success: boolean;
  token?: string;
  refresh_token?: string;
  user?: {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    region_id: number;
  };
  access?: string;
  msg?: string;
}

export class RefreshTokenManager {
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly TOKEN_KEY = 'token';
  private static readonly USER_ID_KEY = 'user_id';
  private static readonly ACCESS_KEY = 'access';
  private static refreshInFlight: Promise<boolean> | null = null;

  /**
   * Attempts to refresh the JWT token using the stored refresh token
   * @returns Promise<boolean> - true if refresh was successful, false otherwise
   */
  static async refreshToken(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefreshToken().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private static async performRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${ApiClient.getCurrentBaseUrl()}/api/users/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
          rotate_token: true // Rotate the refresh token for security
        })
      });

      if (!response.ok) {
        // If refresh fails, clear all tokens
        this.clearTokens();
        return false;
      }

      const data: RefreshTokenResponse = await response.json();
      
      if (data.success && data.token) {
        // Update stored tokens
        localStorage.setItem(this.TOKEN_KEY, data.token);
        localStorage.setItem(this.USER_ID_KEY, data.user?.user_id || '');
        localStorage.setItem(this.ACCESS_KEY, data.access || '');
        
        // Update refresh token if a new one was provided
        if (data.refresh_token) {
          localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refresh_token);
        }
        
        return true;
      }

      return false;
    } catch (error) {
      logger.error('Error refreshing authentication token', error, {
        sourceFile: 'refreshTokenManager.ts',
        sourceClass: 'RefreshTokenManager',
        sourceLayer: 'Utils',
      });
      this.clearTokens();
      return false;
    }
  }

  /**
   * Checks if the current JWT token is expired or will expire soon
   * @param bufferMinutes - Number of minutes before expiry to consider token as "expired"
   * @returns boolean - true if token is expired or will expire soon
   */
  static isTokenExpired(bufferMinutes: number = 5): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      return true;
    }

    try {
      // Decode JWT token to check expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds
      
      return (expiryTime - currentTime) <= bufferTime;
    } catch (error) {
      logger.error('Error parsing JWT token expiry', error, {
        sourceFile: 'refreshTokenManager.ts',
        sourceClass: 'RefreshTokenManager',
        sourceLayer: 'Utils',
      });
      return true;
    }
  }

  /**
   * Attempts to refresh the token if it's expired or will expire soon
   * @param bufferMinutes - Number of minutes before expiry to consider token as "expired"
   * @returns Promise<boolean> - true if token is valid (either existing or refreshed)
   */
  static async ensureValidToken(bufferMinutes: number = 5): Promise<boolean> {
    if (!this.isTokenExpired(bufferMinutes)) {
      return true; // Token is still valid
    }

    return await this.refreshToken();
  }

  /**
   * Clears all stored authentication tokens
   */
  static clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_ID_KEY);
    localStorage.removeItem(this.ACCESS_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Revokes the current refresh token
   * @returns Promise<boolean> - true if revocation was successful
   */
  static async revokeRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return true; // No token to revoke
      }

      const token = localStorage.getItem(this.TOKEN_KEY);
      if (!token) {
        return true; // No JWT token to authenticate the request
      }

      const response = await fetch(`${ApiClient.getCurrentBaseUrl()}/api/users/revoke-refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      return response.ok;
    } catch (error) {
      logger.error('Error revoking refresh token', error, {
        sourceFile: 'refreshTokenManager.ts',
        sourceClass: 'RefreshTokenManager',
        sourceLayer: 'Utils',
      });
      return false;
    }
  }

  /**
   * Checks if user has a refresh token (indicating they chose "stay signed in")
   * @returns boolean - true if refresh token exists
   */
  static hasRefreshToken(): boolean {
    return !!localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }
}
